/* IBM Confidential
 *
 * OCO Source Materials
 *
 * IBM Cognos Products: ba-ui-toolkit
 * (C) Copyright IBM Corp. 2019, 2020
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U.S. Copyright Office.
 * NLS_ENCODING=UTF-8
 * NLS_MESSAGEFORMAT_NONE
 */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { Flyout, ContextFlyout } from '@ba-ui-toolkit/flyout';
import { classnames as cx, propsUtils } from '@ba-ui-toolkit/utils';
import Menu from '@ba-ui-toolkit/menu';

import styles from './ContextMenu.scss';

const contextMenuClassName = styles.menuClassName.replace(/"/g, '');
let candidatePlacement = [
	'left',
	'top',
	'bottom',
	'right',
	'topLeft',
	'bottomLeft',
	'topRight',
	'bottomRight',
];
const { sanitize } = propsUtils;

/** The Context menu is built on top of `<Flyout>`, inheriting much functionality from it.  As such, some of the same properties are made available to context menu as well. */
export default class ContextMenu extends Component {
	static propTypes = {
		/** Where the Context Menu will be positioned visually off of the triggerNode. customPropTypes: ['top', 'bottom', 'left', 'right'] */
		placement: PropTypes.oneOfType([
			(props, propName, componentName) => {
				const value = props[propName];
				switch (value) {
					case 'left':
						console.error(
							new Error(
								`'${value}' supplied to '${propName}' in '${componentName}' is deprecated. It will be removed in Sprint 39.1, use 'topLeft' or 'bottomLeft' instead`
							)
						); // eslint-disable-line no-console
						break;
					case 'right':
						console.error(
							new Error(
								`'${value}' supplied to '${propName}' in '${componentName}' is deprecated. It will be removed in Sprint 39.1, use 'bottomRight' or 'topRight' instead`
							)
						); // eslint-disable-line no-console
						break;
					case 'top':
						console.error(
							new Error(
								`'${value}' supplied to '${propName}' in '${componentName}' is deprecated. It will be removed in Sprint 39.1, use 'topLeft' or 'topRight' instead`
							)
						); // eslint-disable-line no-console
						break;
					case 'bottom':
						console.error(
							new Error(
								`'${value}' supplied to '${propName}' in '${componentName}' is deprecated. It will be removed in Sprint 39.1, use 'bottomLeft' or 'bottomRight' instead`
							)
						); // eslint-disable-line no-console
						break;
					default:
				}

				return null;
			},
			PropTypes.oneOf(candidatePlacement),
		]),
		/** Node that the Context Menu will visually position itself off of */
		triggerNode: PropTypes.instanceOf(Element),
		/** DOM node that the Context Menu will be attached to in the DOM */
		domNodeToAttachTo: PropTypes.instanceOf(Element),
		/** Function that is called when a value in the Context Menu changes (name and value are arguments) */
		onChange: PropTypes.func,
		/** Option to use context to grab the toolkit theme */
		theme: PropTypes.bool,
		/** Function to be called when a click outside of the Context Menu or triggerNode occurs */
		onClose: PropTypes.func,
		/** Class name to be given to the container of Context Menu */
		className: PropTypes.string,
		/** Object that defines which items from each sub-component are selected */
		selected: PropTypes.object,
		/** Sub-components of Context Menu to display (i.e. ContextMenu.List, ContextMenu.Radio, etc.) */
		children: PropTypes.node,
		/** Custom css styles */
		style: PropTypes.object,
		/** Extra options for the menu */
		menuExtraOptions: PropTypes.object,
		/** left/right aligns the context menu to the trigger node */
		align: PropTypes.oneOf(['left', 'right']),
		/** Flyout.propTypes.fixedPosition */
		fixedPosition: Flyout.propTypes.fixedPosition,
	};

	static defaultProps = {
		placement: 'bottomRight',
		className: '',
		style: {},
		menuExtraOptions: {},
	};

	constructor(props) {
		super(props);
		this.state = {
			values: {},
		};
	}

	static Accordion(props) {
		return <Menu.Accordion {...props} />;
	}

	static Spacer(props) {
		return <Menu.Spacer {...props} />;
	}

	static Separator(props) {
		return <Menu.Separator {...props} />;
	}

	static List(props) {
		return <Menu.List {...props} />;
	}

	static Radio(props) {
		return <Menu.Radio {...props} />;
	}

	static Checkbox(props) {
		return <Menu.Checkbox {...props} />;
	}

	static ToggleSwitch(props) {
		return <Menu.ToggleSwitch {...props} />;
	}

	_onChange = (name, item, isDisabled) => {
		const { onChange } = this.props;
		onChange && onChange(name, item, isDisabled);
	};

	render() {
		const {
			align,
			triggerNode,
			theme,
			onClose,
			fixedPosition,
			domNodeToAttachTo,
			className,
			children,
			selected,
			menuExtraOptions,
			...others
		} = this.props;
		const classNames = cx(contextMenuClassName);
		let { placement, style } = this.props;
		let alignStyles = {};
		if (triggerNode && align && (align === 'left' || align === 'right')) {
			const bodyRect = document.body.getBoundingClientRect();
			const contextRect = triggerNode.getBoundingClientRect();
			const offsetTop = Math.abs(bodyRect.top - contextRect.top) + contextRect.height;
			const offsetRight = Math.abs(bodyRect.right - contextRect.right);
			switch (align) {
				case 'left':
					candidatePlacement = [];
					placement = 'bottomRight';
					alignStyles = {
						left: contextRect.left,
						top: offsetTop,
					};
					break;
				case 'right':
					candidatePlacement = [];
					placement = 'bottomLeft';
					alignStyles = {
						right: offsetRight,
						left: null,
						top: offsetTop,
					};
					break;
				default:
					break;
			}
		}
		style = {
			...alignStyles,
			width: 'auto',
			minWidth: '200px',
			...style,
		};
		const flyoutProps = {
			...sanitize(others, ContextMenu),
			placement,
			hideArrow: true,
			className: `${classNames} ${className}`,
			offset: 8, // 8 pixel distance for the context menu defined by design
			fixedPosition,
			triggerNode,
			onClose,
			domNodeToAttachTo,
			style,
			candidatePlacement,
		};

		const menuClassName = cx({ contextMenu: true });

		const menuProps = {
			'data-tid': 'ba-common-contextmenu__menu',
			...sanitize(menuExtraOptions, ContextMenu),
			className: menuClassName,
			selected,
			onChange: this._onChange,
		};

		const menu = <Menu {...menuProps}>{children}</Menu>;

		const contextFlyout = (
			<ContextFlyout
				{...flyoutProps}
				ref={node => {
					this._contextMenu = node;
				}}
			>
				{menu}
			</ContextFlyout>
		);

		const normalFlyout = (
			<Flyout
				{...flyoutProps}
				ref={node => {
					this._contextMenu = node;
				}}
			>
				{menu}
			</Flyout>
		);

		return theme ? contextFlyout : normalFlyout;
	}
}
